export type Platform =
  | 'ios-safari'
  | 'ios-other'
  | 'android-chrome'
  | 'android-firefox'
  | 'desktop-chromium'
  | 'desktop-safari'
  | 'unsupported';

export type InstallStep = {
  icon: string;
  title: string;
  description: string;
  locationHint?: string;
  tip?: string;
};

export type PlatformInstructions = {
  platform: Platform;
  canInstall: boolean;
  title: string;
  intro?: string;
  timeEstimate?: string;
  steps: InstallStep[];
  afterInstalling?: string[];
  troubleshooting?: string[];
  note?: string;
  warning?: string;
  showOpenInSafari?: boolean;
};

export const getPlatform = (): Platform => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);
  const isChrome = /Chrome/.test(ua) || /CriOS/.test(ua);
  const isFirefox = /Firefox/.test(ua) || /FxiOS/.test(ua);
  const isEdge = /Edg/.test(ua);

  if (isIOS) {
    return isSafari ? 'ios-safari' : 'ios-other';
  }

  if (isAndroid) {
    if (isChrome) return 'android-chrome';
    if (isFirefox) return 'android-firefox';
    return 'unsupported';
  }

  // Desktop
  if (isSafari) return 'desktop-safari';
  if (isChrome || isEdge) return 'desktop-chromium';
  
  return 'unsupported';
};

export const canInstallPWA = (platform: Platform): boolean => {
  return ['ios-safari', 'android-chrome', 'android-firefox', 'desktop-chromium'].includes(platform);
};

/**
 * Check if the app is running as an installed PWA (standalone mode)
 */
export const isPWA = (): boolean => {
  // Check display-mode media query
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // iOS Safari
  if ((window.navigator as any).standalone === true) {
    return true;
  }
  
  return false;
};

export const getPlatformInstructions = (platform: Platform): PlatformInstructions => {
  switch (platform) {
    case 'ios-safari':
      return {
        platform,
        canInstall: true,
        title: 'Install on iPhone/iPad',
        intro: 'Wormble is a Progressive Web App (PWA) — a modern way to install apps directly from your browser. Unlike App Store apps, PWAs are lightweight, always up-to-date, and don\'t require Apple\'s approval process. This is the same technology used by Twitter, Starbucks, and Pinterest. It\'s completely safe and gives you the full app experience.',
        timeEstimate: '⏱️ Takes about 30 seconds',
        steps: [
          {
            icon: '📤',
            title: 'Tap the Share button',
            description: 'Look for the square icon with an arrow pointing up',
            locationHint: 'Bottom center of Safari (or top right on iPad)',
            tip: 'The Share button is in the Safari toolbar, not at the top of the screen'
          },
          {
            icon: '📜',
            title: 'Scroll down the menu',
            description: 'Swipe up through the share menu options',
            locationHint: 'In the bottom sheet that appears',
            tip: 'The option you need is below the row of app icons'
          },
          {
            icon: '➕',
            title: "Find 'Add to Home Screen'",
            description: "Look for the option with a plus icon and home screen symbol",
            locationHint: 'Usually about halfway down the list'
          },
          {
            icon: '✅',
            title: 'Tap Add to confirm',
            description: "Review the app name and tap 'Add' in the top right corner",
            locationHint: 'Top right of the confirmation screen'
          },
        ],
        afterInstalling: [
          "Look for the Wormble icon on your home screen",
          "Tap the icon to launch the app",
          "The app opens in fullscreen without Safari's browser bar",
          "Your progress and streaks are saved automatically"
        ],
        troubleshooting: [
          "Don't see the Share button? Make sure you're using Safari, not another browser",
          "Can't find 'Add to Home Screen'? Update to iOS 16 or later for best results",
          "Already installed? Check your home screen or App Library for the Wormble icon"
        ],
        note: "After installing, launch Wormble from your home screen for the full app experience!",
      };

    case 'ios-other':
      return {
        platform,
        canInstall: false,
        title: 'Install on iPhone/iPad',
        intro: 'Progressive Web Apps can only be installed through Safari on iOS due to Apple\'s App Store policies. Switch to Safari to get the full Wormble app experience with offline play and home screen access.',
        timeEstimate: '⏱️ Takes about 1 minute',
        steps: [],
        warning: 'You\'re currently using Chrome, Firefox, or another browser. PWAs can only be installed using Safari on iPhone and iPad due to Apple\'s App Store policies.',
        troubleshooting: [
          'Copy this page URL and paste it in Safari',
          'Or tap "Open in Safari" below to switch browsers',
          'Once in Safari, follow the installation steps'
        ],
        showOpenInSafari: true,
      };

    case 'android-chrome':
      return {
        platform,
        canInstall: true,
        title: 'Install on Android',
        intro: 'Wormble installs as a Progressive Web App (PWA) — a lightweight app that lives on your device without going through the Play Store. PWAs load faster than traditional apps, use minimal storage, and update automatically. Google actively supports this technology in Chrome.',
        timeEstimate: '⏱️ Takes about 20 seconds',
        steps: [
          {
            icon: '⋮',
            title: 'Open the menu',
            description: 'Tap the three vertical dots',
            locationHint: 'Top right corner of Chrome',
            tip: 'You may see a small install icon (⊕) in the address bar instead'
          },
          {
            icon: '📥',
            title: "Select 'Install app'",
            description: "Look for 'Install app', 'Add to Home screen', or 'Install Wormble'",
            locationHint: 'Near the top of the menu',
            tip: 'The exact wording may vary depending on your Chrome version'
          },
          {
            icon: '✅',
            title: 'Confirm installation',
            description: "Tap 'Install' in the popup dialog",
            tip: 'Chrome may ask for permissions - these are safe to allow'
          },
        ],
        afterInstalling: [
          "Wormble appears in your app drawer and home screen",
          "Launch it like any other Android app",
          "Runs in fullscreen without browser chrome",
          "Works offline and syncs when connected"
        ],
        troubleshooting: [
          "Don't see 'Install app'? Try updating Chrome to the latest version",
          "Option is grayed out? You may have already installed it - check your app drawer",
          "Still having issues? Try clearing Chrome's cache and reloading this page"
        ],
        note: 'Launch Wormble from your app drawer for the best experience!',
      };

    case 'android-firefox':
      return {
        platform,
        canInstall: true,
        title: 'Install on Android',
        intro: 'Install Wormble to play without the browser interface.',
        timeEstimate: '⏱️ Takes about 20 seconds',
        steps: [
          {
            icon: '⋮',
            title: 'Open Firefox menu',
            description: 'Tap the three vertical dots',
            locationHint: 'Top right or bottom right, depending on your Firefox settings'
          },
          {
            icon: '📥',
            title: "Find the install option",
            description: "Look for 'Install' or 'Add to Home screen'",
            locationHint: 'In the main menu list',
            tip: 'Firefox uses different wording than Chrome'
          },
          {
            icon: '✅',
            title: 'Complete installation',
            description: 'Confirm by tapping the install button',
            tip: 'Firefox will add the icon to your home screen'
          },
        ],
        afterInstalling: [
          "Wormble icon appears on your home screen",
          "Tap to open in standalone mode",
          "Full offline support included",
          "Syncs progress when online"
        ],
        troubleshooting: [
          "Can't find the option? Update Firefox to the latest version",
          "Already installed? Check your home screen for the icon",
          "Want to use Chrome instead? Chrome offers a more app-like experience"
        ],
        note: "Firefox adds Wormble directly to your home screen, not the app drawer.",
      };

    case 'desktop-chromium':
      return {
        platform,
        canInstall: true,
        title: 'Install on Desktop',
        intro: 'Install Wormble as a desktop app using Progressive Web App technology. PWAs run in their own window without browser tabs, launch faster than websites, and work offline. No download or installation wizard required — it\'s instant.',
        timeEstimate: '⏱️ Takes about 10 seconds',
        steps: [
          {
            icon: '⊕',
            title: 'Click the install icon',
            description: 'Look for the install icon (⊕ or computer/phone icon) in the address bar',
            locationHint: 'Right side of the address bar, next to the bookmark star',
            tip: 'The icon appears automatically when a site can be installed'
          },
          {
            icon: '⋮',
            title: 'Or use the browser menu',
            description: "Click the three-dot menu → 'Install Wormble' or 'Apps' → 'Install this site as an app'",
            locationHint: 'Top right corner of Chrome/Edge',
            tip: 'This method works even if the address bar icon doesn\'t appear'
          },
        ],
        afterInstalling: [
          "Wormble opens in its own window without browser tabs",
          "Find it in your Start Menu (Windows) or Applications folder (Mac)",
          "Pin to taskbar or dock for one-click access",
          "Launches faster than opening a browser tab",
          "All your progress syncs automatically"
        ],
        troubleshooting: [
          "Don't see the install icon? The site may already be installed",
          "Check your apps list: Windows Start Menu or Mac Applications folder",
          "To reinstall: Uninstall the existing app from chrome://apps, then reload this page",
          "Edge users: Look for 'Apps' in the menu instead of 'Install'"
        ],
        note: 'Desktop installed apps run in their own window and appear in your apps list!',
      };

    case 'desktop-safari':
      return {
        platform,
        canInstall: false,
        title: 'Limited PWA Support',
        intro: 'Safari on macOS doesn\'t support installing web apps to your dock.',
        timeEstimate: '⏱️ 30 seconds with Chrome',
        steps: [],
        warning: 'Safari on Mac has limited Progressive Web App support. You can still use Wormble in Safari, but it won\'t install as a standalone application.',
        troubleshooting: [
          'For the best experience, use Chrome or Edge browsers',
          'Or simply bookmark wormble.com in Safari for quick access',
          'Your game progress will still save in Safari\'s browser version'
        ],
        note: 'Chrome and Edge offer full installation support on Mac with desktop app features.',
      };

    case 'unsupported':
    default:
      return {
        platform,
        canInstall: false,
        title: 'Browser Not Supported',
        intro: 'Your browser doesn\'t support installing web apps.',
        timeEstimate: '⏱️ 30 seconds in supported browser',
        steps: [],
        warning: "Your current browser doesn't support Progressive Web App installation. However, you can still play Wormble directly in your browser!",
        troubleshooting: [
          'For installation on iOS: Open this page in Safari',
          'For installation on Android: Open this page in Chrome or Firefox',
          'For installation on Desktop: Use Chrome or Edge browsers',
          'Bookmark this page for quick access in any browser'
        ],
        note: 'Recommended browsers: Safari (iOS), Chrome (Android/Desktop), Edge (Desktop)',
      };
  }
};
